/**
 * Deterministic hash noise. Every worldgen decision flows from these functions,
 * so they must return bit-identical values on every machine: integer math only
 * (`Math.imul`), division by a power of two, no trig, no `Math.random`, no
 * clock reads. Ported from the prototype's hash3/value-noise/fbm with one fix:
 * the final division is 2^32 instead of 2^32-1, so the range is [0, 1) — the
 * prototype could return exactly 1.0, which breaks `< frequency` ore rolls.
 *
 * Coordinates passed to `hash3` are expected to be integers; fractional inputs
 * are truncated, which keeps the integer pipeline intact.
 */

/** Integer lattice hash, uniform in [0, 1). Bit-identical on any JS engine. */
export function hash3(seed: number, x: number, y: number, z: number): number {
  let h =
    (seed | 0) +
    Math.imul(x | 0, 374761393) +
    Math.imul(y | 0, 668265263) +
    Math.imul(z | 0, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Hermite smoothstep on a lattice fraction, same curve the prototype used. */
function fade(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Smoothly interpolated 2D value noise in [0, 1). */
export function valueNoise2(x: number, z: number, seed: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const u = fade(x - xi);
  const v = fade(z - zi);
  const a = hash3(seed, xi, 0, zi);
  const b = hash3(seed, xi + 1, 0, zi);
  const c = hash3(seed, xi, 0, zi + 1);
  const d = hash3(seed, xi + 1, 0, zi + 1);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

/** Smoothly interpolated 3D value noise in [0, 1). */
export function valueNoise3(x: number, y: number, z: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const u = fade(x - xi);
  const v = fade(y - yi);
  const w = fade(z - zi);
  const c000 = hash3(seed, xi, yi, zi);
  const c100 = hash3(seed, xi + 1, yi, zi);
  const c010 = hash3(seed, xi, yi + 1, zi);
  const c110 = hash3(seed, xi + 1, yi + 1, zi);
  const c001 = hash3(seed, xi, yi, zi + 1);
  const c101 = hash3(seed, xi + 1, yi, zi + 1);
  const c011 = hash3(seed, xi, yi + 1, zi + 1);
  const c111 = hash3(seed, xi + 1, yi + 1, zi + 1);
  return lerp(
    lerp(lerp(c000, c100, u), lerp(c010, c110, u), v),
    lerp(lerp(c001, c101, u), lerp(c011, c111, u), v),
    w,
  );
}

/**
 * Fractal brownian motion over value noise, normalised to [0, 1). Each octave
 * gets its own seed salt so the layers do not correlate.
 */
export function fbm2(x: number, z: number, octaves: number, seed: number): number {
  let sum = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise2(x * frequency, z * frequency, seed + i * 131) * amplitude;
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return sum / norm;
}

/** 3D fbm, normalised to [0, 1). Used for caves. */
export function fbm3(x: number, y: number, z: number, octaves: number, seed: number): number {
  let sum = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise3(x * frequency, y * frequency, z * frequency, seed + i * 97) * amplitude;
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return sum / norm;
}
